import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { instrumentedFetch } from './instrumentation';

export type GatheredHQSupabaseClient = SupabaseClient<Database>;

/**
 * Browser client — uses anon key, RLS enforced.
 * Use this in frontend apps (dashboard, guest).
 *
 * @param storage - Pass `window.sessionStorage` for tab-isolated sessions
 *   (each tab can be logged in as a different user). Default: `window.localStorage`
 *   (session shared across all tabs, persists after tab close).
 */
export function createBrowserClient(
  supabaseUrl: string,
  supabaseAnonKey: string,
  storageKey = 'sb-dashboard-auth-token',
  storage?: Storage
): GatheredHQSupabaseClient {
  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      storageKey,
      storage,
      flowType: 'pkce',
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });
}

/**
 * Admin client — uses service role key, bypasses RLS.
 * ONLY for use in apps/api (server-side).
 * NEVER import this in any frontend app.
 */
export function createAdminClient(
  supabaseUrl: string,
  serviceRoleKey: string
): GatheredHQSupabaseClient {
  return createClient<Database>(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
    // Server-side only. Reports PostgREST pool exhaustion (PGRST003) and latency to
    // whatever observer apps/api installed; a no-op passthrough when none is set.
    global: { fetch: instrumentedFetch },
  });
}
